import type {ReactElement} from 'react';
import React from 'react';
import {BaseGammaOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-gamma-option';
import {GammaColorType} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-gamma-option/base-gamma-option.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromGammaOfActiveSlideItem} from '@Hooks/poster-editor/useGetKeyFromGammaOfActiveSlideItem';

export function SlideshowItemGammaOption(): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const isEnabled = useGetKeyFromGammaOfActiveSlideItem('isEnabled');
  const gammaRed = useGetKeyFromGammaOfActiveSlideItem('red');
  const gammaGreen = useGetKeyFromGammaOfActiveSlideItem('green');
  const gammaBlue = useGetKeyFromGammaOfActiveSlideItem('blue');

  if (!activeItem || !activeSlideUID) {
    throw new Error('Active item or active slide is not defined in slideshow item effects options');
  }

  if (!activeItem.isSlideshow()) {
    throw new Error(`Expected active item to be of type slideshow, got ${activeItem.gitype} instead`);
  }

  if (isEnabled === undefined || gammaRed === undefined || gammaGreen === undefined || gammaBlue === undefined) {
    throw new Error('gamma properties are undefined for active slide item');
  }

  const onToggleGamma = (): void => {
    void activeItem.updateSlideFromObject(activeSlideUID, {
      effects: {
        gamma: {
          isEnabled: !isEnabled,
        },
      },
    });
  };

  const updateRedColor = (red: number, undoable = true): void => {
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        effects: {
          gamma: {
            red,
          },
        },
      },
      {
        undoable,
      }
    );
  };

  const updateGreenColor = (green: number, undoable = true): void => {
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        effects: {
          gamma: {
            green,
          },
        },
      },
      {
        undoable,
      }
    );
  };

  const updateBlueColor = (blue: number, undoable = true): void => {
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        effects: {
          gamma: {
            blue,
          },
        },
      },
      {
        undoable,
      }
    );
  };

  const onUpdateColor = (color: GammaColorType, updatedValue: number, undoable = true): void => {
    switch (color) {
      case GammaColorType.RED: {
        updateRedColor(updatedValue, undoable);
        return;
      }
      case GammaColorType.GREEN: {
        updateGreenColor(updatedValue, undoable);
        return;
      }
      case GammaColorType.BLUE: {
        updateBlueColor(updatedValue, undoable);
        return;
      }
      default:
        throw new Error('Unhandled gamma color type');
    }
  };

  return (
    <BaseGammaOption
      gamma={{
        isEnabled,
        red: gammaRed,
        blue: gammaBlue,
        green: gammaGreen,
      }}
      updateColor={onUpdateColor}
      onToggleGamma={onToggleGamma}
    />
  );
}
